import { useEffect, useMemo, useRef } from "react"

import type { AnimationEvent } from "@/types"

import {
  getActionBadgeStyle,
  getActionBorderColor,
} from "../replay/action-colors"

interface MiniEventLogProps {
  events: AnimationEvent[]
  currentEventIndex: number
  homeTeamId: string
  homeTeamHue: number
  awayTeamHue: number
}

function getActionType(event: AnimationEvent): string | null {
  const header = event.detailHeader
  if (
    typeof header === "object" &&
    header !== null &&
    "action_type" in header
  ) {
    return header.action_type ?? null
  }
  return null
}

function getPlayerName(event: AnimationEvent): string | null {
  const header = event.detailHeader
  if (
    typeof header === "object" &&
    header !== null &&
    "player_name" in header
  ) {
    return header.player_name ?? null
  }
  if (typeof header === "string") return header
  return null
}

type Row =
  | { kind: "header"; turnLabel: string; teamHue: number; eventIndex: number }
  | { kind: "event"; event: AnimationEvent; eventIndex: number }

const EVENT_ROW_HEIGHT = 46
const HEADER_ROW_HEIGHT = 28

export function MiniEventLog({
  events,
  currentEventIndex,
  homeTeamId,
  homeTeamHue,
  awayTeamHue,
}: MiniEventLogProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  // Build a flat list of rows: turn headers interspersed with event rows
  const rows = useMemo(() => {
    const result: Row[] = []
    let lastTurnLabel: string | null = null

    for (let i = 0; i < events.length; i++) {
      const event = events[i]
      if (event.turnLabel && event.turnLabel !== lastTurnLabel) {
        const teamHue =
          event.actingTeamId === homeTeamId ? homeTeamHue : awayTeamHue
        result.push({
          kind: "header",
          turnLabel: event.turnLabel,
          teamHue,
          eventIndex: i,
        })
        lastTurnLabel = event.turnLabel
      }
      result.push({ kind: "event", event, eventIndex: i })
    }
    return result
  }, [events, homeTeamId, homeTeamHue, awayTeamHue])

  // Scroll the active event row into view
  useEffect(() => {
    const el = containerRef.current
    if (!el || currentEventIndex < 0) return
    const rowIndex = rows.findIndex(
      (r) => r.kind === "event" && r.eventIndex === currentEventIndex,
    )
    if (rowIndex < 0) return
    // Calculate pixel offset by summing row heights up to this row
    let offset = 0
    for (let i = 0; i < rowIndex; i++) {
      offset += rows[i].kind === "header" ? HEADER_ROW_HEIGHT : EVENT_ROW_HEIGHT
    }
    // Center it in the 3-row viewport
    const target = offset - EVENT_ROW_HEIGHT
    el.scrollTo({ top: Math.max(0, target), behavior: "smooth" })
  }, [currentEventIndex, rows])

  return (
    <div
      ref={containerRef}
      className="w-full overflow-y-auto"
      style={{
        height: EVENT_ROW_HEIGHT * 3,
        background: "oklch(0.10 0.02 55 / 0.95)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        borderTop: "1px solid oklch(0.30 0.05 55)",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      {rows.map((row) => {
        if (row.kind === "header") {
          return (
            <div
              key={`header-${row.eventIndex}`}
              className="font-display flex items-center gap-2 px-3 text-[10px] font-bold tracking-[0.15em] uppercase"
              style={{
                height: HEADER_ROW_HEIGHT,
                background: "oklch(0.14 0.02 55 / 0.6)",
                borderBottom: "1px solid oklch(0.20 0.02 55)",
              }}
            >
              <span
                className="rounded-full"
                style={{
                  width: 6,
                  height: 6,
                  background: `oklch(0.65 0.15 ${row.teamHue})`,
                  boxShadow: `0 0 4px oklch(0.65 0.15 ${row.teamHue} / 0.5)`,
                }}
              />
              <span style={{ color: `oklch(0.65 0.10 ${row.teamHue})` }}>
                {row.turnLabel}
              </span>
            </div>
          )
        }

        const { event, eventIndex } = row
        const isActive = eventIndex === currentEventIndex
        const isPast = currentEventIndex >= 0 && eventIndex < currentEventIndex
        const actionType = getActionType(event)
        const badge = getActionBadgeStyle(actionType)
        const borderColor = getActionBorderColor(actionType)
        const playerName = getPlayerName(event)
        const teamHue =
          event.actingTeamId === homeTeamId ? homeTeamHue : awayTeamHue

        return (
          <div
            key={`event-${eventIndex}`}
            className="flex items-center gap-2 overflow-hidden px-3 transition-opacity duration-200"
            style={{
              height: EVENT_ROW_HEIGHT,
              opacity: isActive ? 1 : isPast ? 0.5 : 0.35,
              background: isActive
                ? "oklch(0.18 0.02 55 / 0.5)"
                : "transparent",
              borderLeft: isActive
                ? `3px solid ${borderColor}`
                : "3px solid transparent",
            }}
          >
            {/* Action badge */}
            {actionType && (
              <span
                className="flex-shrink-0 rounded-sm px-1.5 py-0.5 text-[9px] font-bold tracking-wider uppercase"
                style={{
                  background: badge.bg,
                  color: badge.text,
                  border: `1px solid ${badge.border}`,
                }}
              >
                {actionType}
              </span>
            )}

            {/* Content */}
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-1.5">
                {playerName && (
                  <span
                    className="text-xs font-bold"
                    style={{
                      color: `oklch(0.75 0.12 ${teamHue})`,
                    }}
                  >
                    {playerName}
                  </span>
                )}
                {event.play && (
                  <span
                    className="text-[10px] tracking-wide"
                    style={{ color: "oklch(0.50 0.03 55)" }}
                  >
                    {event.play}
                  </span>
                )}
              </div>
              {event.detailNarrative && (
                <p
                  className="line-clamp-2 text-[11px] leading-tight"
                  style={{ color: "oklch(0.55 0.03 55)" }}
                >
                  {event.detailNarrative}
                </p>
              )}
            </div>

            {/* Highlight type indicator */}
            {event.highlightType === "touchdown" && (
              <span
                className="flex-shrink-0 text-[9px] font-bold tracking-wider uppercase"
                style={{ color: "oklch(0.78 0.18 55)" }}
              >
                TD
              </span>
            )}
            {event.highlightType === "turnover" && (
              <span
                className="flex-shrink-0 text-[9px] font-bold tracking-wider uppercase"
                style={{ color: "oklch(0.55 0.18 28)" }}
              >
                TO
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}
